import { Box, Typography } from '@mui/material'

export type StreamingStatusIndicatorProps = {
  label: string | null
}

const spinnerSx = {
  display: 'inline-block',
  width: 12,
  height: 12,
  borderRadius: '50%',
  border: '2px solid',
  borderColor: 'text.disabled',
  borderTopColor: 'text.secondary',
  animation: 'stream-spin 0.8s linear infinite',
  '@keyframes stream-spin': {
    to: { transform: 'rotate(360deg)' }
  },
  flexShrink: 0
}

/**
 * Inline status label shown inside the in-progress assistant message bubble
 * while a tool is executing. Renders nothing when label is null.
 * Replaces in-place as tools fire in sequence — never stacks.
 *
 * Purely visual: marked aria-hidden so screen readers ignore it. The bubble
 * rerenders on every typewriter tick (25fps) and NVDA + Chrome can re-announce
 * a role="status" region on each rerender even when text is unchanged. The
 * announcement is owned by a separate, persistent live region in AssistantChat
 * (StreamingStatusAnnouncer) that only rerenders when status changes.
 */
const StreamingStatusIndicator = ({ label }: StreamingStatusIndicatorProps) => (
  <Box aria-hidden>
    {label !== null && (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.5 }}>
        <Box data-testid="streaming-spinner" sx={spinnerSx} />
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          {label}
        </Typography>
      </Box>
    )}
  </Box>
)

export default StreamingStatusIndicator
